import * as React from 'react';
import Media from 'react-media';

import classNames from 'classnames';
import gql from 'graphql-tag';

import Imgix from 'js/components/Imgix';
import imgix from 'js/lib/imgix';

import { AlwaysLightMode, Typography } from '@coursera/cds-core';

import type { Partner } from 'bundles/degree-home/types/Degree';
import type { UserAgent } from 'bundles/item-lti/constants/types';
import { MOBILE_BREAKPOINT_PX } from 'bundles/page-footer/components/global-footer/constants';
import MobileBreadCrumb from 'bundles/page/components/MobileBreadCrumb';
import DdpBreadcrumbsContainer from 'bundles/premium-certificate/components/DdpBreadcrumbsContainer';

import _t from 'i18n!nls/degree-home';

import 'css!./__styl__/DegreeHeader';

type Props = {
  headerImageUrl: string;
  degreeName: string;
  categoryData?: {
    name: string;
    slug: string;
  };
  fadeOnScroll?: boolean;
  partner: Partner;
  gradientColors?: Array<string>;
  secondaryPartnerLogo?: string;
  userAgent: UserAgent;
};

type State = {
  headerOpacity: number;
  isFixed: boolean;
  offsetTop: number;
};

type Element = {
  clientHeight: number;
  getBoundingClientRect: Function;
};

class DegreeHeader extends React.Component<Props, State> {
  state = {
    headerOpacity: 1,
    isFixed: false,
    offsetTop: 65,
  };

  componentDidMount() {
    window.addEventListener('scroll', this.onScroll);
    this.onScroll();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll = () => {
    const { fadeOnScroll } = this.props;

    if (!fadeOnScroll) {
      this.setState({ headerOpacity: 1 });
      return;
    }

    const { scrollY } = window;
    const {
      // @ts-expect-error TSMIGRATION
      el: { clientHeight },
    } = this;
    const { offsetTop } = this.state;
    const maxScrollTop = clientHeight;
    const percent = Math.min(1, scrollY / maxScrollTop);
    const clampedPercent = percent / 1.5;

    this.setState({
      headerOpacity: 1 - clampedPercent,
      isFixed: scrollY >= offsetTop,
    });
  };

  getBackgroundStyle() {
    return {
      background: this.buildLinearGradient(),
    };
  }

  getContainerStyle() {
    const { headerImageUrl } = this.props;
    const { headerOpacity } = this.state;
    const IMGIX_PARAMS = {
      height: 600,
      width: 2000,
      fit: 'crop',
    };
    const processedImageUrl = imgix.processImage(headerImageUrl, IMGIX_PARAMS);

    return {
      backgroundImage: `url(${processedImageUrl})`,
      opacity: headerOpacity,
    };
  }

  getGradientStyle() {
    return {
      background: this.buildLinearGradient(),
    };
  }

  el: Element | null = null;

  buildLinearGradient() {
    const { gradientColors } = this.props;
    const defaultGradientColors = ['var(--cds-color-callouts-tertiary)', 'var(--cds-color-blue-975)'];
    const firstGradientColor = (gradientColors && gradientColors[1]) || defaultGradientColors[1];
    const secondGradientColor = (gradientColors && gradientColors[0]) || defaultGradientColors[0];

    return `linear-gradient(324.97deg, ${firstGradientColor} 0%, ${secondGradientColor} 100%)`;
  }

  static fragments = {
    partners: gql`
      fragment DegreeHeaderPartners on PartnersV1Connection {
        elements {
          id
          name
          rectangularLogo
        }
      }
    `,
    colors: gql`
      fragment DegreeHeaderColors on DegreesV1 {
        headerBackgroundColor {
          ... on DegreesV1_linearGradient2Member {
            linearGradient2 {
              color1 {
                red
                green
                blue
              }
              color2 {
                red
                green
                blue
              }
            }
          }
        }
      }
    `,
  };

  renderBreadcrumbs() {
    const { degreeName, categoryData } = this.props;
    if (!categoryData || !degreeName) return null;

    const links = [
      {
        label: 'Degrees',
        href: '/degrees',
      },
      {
        label: _t('#{category} Degrees', { category: categoryData.name }),
        href: `/degrees/${categoryData.slug}`,
      },
      {
        label: degreeName,
      },
    ];

    return <DdpBreadcrumbsContainer links={links} />;
  }

  renderMobileBreadCrumb() {
    const { degreeName, categoryData } = this.props;
    if (!categoryData || !degreeName) return null;

    const { label, href } = {
      label: _t('#{category} Degrees', { category: categoryData.name }),
      href: `/degrees/${categoryData.slug}`,
    };

    return <MobileBreadCrumb href={href} label={label} />;
  }

  render() {
    const { degreeName, partner, secondaryPartnerLogo, userAgent } = this.props;
    const { isFixed } = this.state;

    const rectangularLogo = partner ? partner.rectangularLogo : '';
    const logoAltText = partner ? `${partner.name} ${_t('Logo')}` : _t('Logo');

    const maxLength = 80;

    let trimmedDegreeName = degreeName.substr(0, maxLength);
    trimmedDegreeName = trimmedDegreeName.substr(
      0,
      Math.min(trimmedDegreeName.length, trimmedDegreeName.lastIndexOf(' '))
    );

    const showTrimmedName = degreeName.length > maxLength;

    return (
      <AlwaysLightMode className="degree-header-sticky-wrapper">
        <div
          className={classNames('rc-DegreeHeader', { fixed: isFixed })}
          style={this.getBackgroundStyle()}
          ref={(el) => {
            this.el = el;
          }}
        >
          <div className="header-container" style={this.getContainerStyle()}>
            <div className="gradient-background" style={this.getGradientStyle()} />

            <div className="content-container bt3-container horizontal-box">
              <div className="content bt3-col-xs-12 bt3-col-sm-12 bt3-col-md-12 bt3-col-lg-12">
                <Media
                  query={{ minWidth: MOBILE_BREAKPOINT_PX }}
                  defaultMatches={userAgent && !userAgent.isMobileBrowser}
                >
                  {(matches) => {
                    if (!matches) {
                      return this.renderMobileBreadCrumb();
                    }
                    return this.renderBreadcrumbs();
                  }}
                </Media>

                <Typography variant="d1" className="degree-name" color="invertBody">
                  {showTrimmedName ? `${trimmedDegreeName}...` : degreeName}
                </Typography>

                {rectangularLogo && <Imgix src={rectangularLogo} alt={logoAltText} className="degree-logo" />}

                {secondaryPartnerLogo && (
                  <Imgix src={secondaryPartnerLogo} alt={logoAltText} className="degree-logo secondary-logo" />
                )}
              </div>
            </div>
          </div>
        </div>
      </AlwaysLightMode>
    );
  }
}

export default DegreeHeader;
